import { isPlatformBrowser } from '@angular/common';
import {
  DestroyRef,
  Injectable,
  InjectionToken,
  PLATFORM_ID,
  inject,
  signal,
} from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { NavigationEnd, Router } from '@angular/router';
import { SwUpdate, VersionEvent } from '@angular/service-worker';
import { environment } from 'environments/environment';

export const APP_UPDATE_IDLE_MS = 5 * 60 * 1000;
export const APP_UPDATE_HIDDEN_MS = 2 * 60 * 1000;
/** Longest a reload waits for a download in progress before loading anyway. */
export const APP_UPDATE_WAIT_MS = 15 * 1000;
export const APP_UPDATE_SCROLL_KEY = 'app-update-scroll';
export const APP_UPDATE_AUTO_APPLY_KEY = 'app-update-auto-applied';

/**
 * Browsers skip view transitions on reload, so load the same URL instead.
 * With a #fragment that would only scroll, so reload.
 */
export const APP_UPDATE_RELOAD = new InjectionToken<() => void>(
  'APP_UPDATE_RELOAD',
  {
    factory: () => () =>
      location.hash ? location.reload() : location.replace(location.href),
  }
);

export const APP_UPDATE_ASSIGN = new InjectionToken<(url: string) => void>(
  'APP_UPDATE_ASSIGN',
  { factory: () => url => location.assign(url) }
);

export type UpdateState =
  | 'idle'
  | 'checking'
  | 'downloading'
  | 'ready'
  | 'reloading';

const SCROLL_SELECTOR = '.main-content-container';
const SCROLL_VERSION = 1;
/** List data arrives after NavigationEnd, so the saved offset may not exist yet. */
const SCROLL_RESTORE_MS = 5000;

interface ScrollSnapshot {
  version: number;
  url: string;
  scrollTop: number;
}

export function logAppUpdate(message: string, details?: unknown): void {
  if (!environment.debugMode) {
    return;
  }
  /* eslint-disable no-console -- debugMode is the gate */
  if (details === undefined) {
    console.info('[app-update]', message);
  } else {
    console.info('[app-update]', message, details);
  }
  /* eslint-enable no-console */
}

/** Latest constructed service. Older test instances ignore window events. */
let latestServiceId = 0;

@Injectable({
  providedIn: 'root',
})
export class AppUpdateService {
  private readonly platformId = inject(PLATFORM_ID);
  private readonly destroyRef = inject(DestroyRef);
  private readonly router = inject(Router);
  private readonly swUpdate = inject(SwUpdate, { optional: true });
  private readonly reloadPage = inject(APP_UPDATE_RELOAD);
  private readonly assignPage = inject(APP_UPDATE_ASSIGN);
  private readonly serviceId = ++latestServiceId;

  private readonly stateWritable = signal<UpdateState>('idle');
  readonly state = this.stateWritable.asReadonly();

  /**
   * Set by VERSION_READY. The next document load runs the new build.
   * Setting state to reloading does not clear it.
   */
  updateReady = false;

  private preparation: Promise<void> | null = null;
  private settlePreparation: (() => void) | null = null;
  /** True while the check started by this page load is running. */
  private startupCheck = false;
  private interacted = false;
  private announced = false;
  private watching = false;
  private scrollRestored = false;
  private idleTimer: ReturnType<typeof setTimeout> | null = null;
  private hiddenAt: number | null = null;
  private lastActivityAt = 0;

  constructor() {
    this.destroyRef.onDestroy(() => this.clearIdle());
  }

  /** The API reported a new deployment. Arms the idle and hidden-tab reload. */
  announceUpdate(): void {
    this.noteAnnounced();
  }

  /**
   * Asks the worker for a new build, one check at a time. Resolves when the
   * build is ready, when there is none, or when installing it failed.
   */
  prepare(): Promise<void> {
    if (this.updateReady) {
      return Promise.resolve();
    }
    if (this.preparation) {
      logAppUpdate('prepare already in flight', {
        state: this.stateWritable(),
      });
      return this.preparation;
    }
    const sw = this.controlledWorker();
    if (!sw) {
      logAppUpdate('prepare skipped, page is not controlled by the worker');
      return Promise.resolve();
    }
    logAppUpdate('prepare checking');
    if (this.stateWritable() === 'idle') {
      this.stateWritable.set('checking');
    }
    this.preparation = new Promise<void>(resolve => {
      this.settlePreparation = resolve;
    });
    void sw.checkForUpdate().then(
      found => {
        logAppUpdate('checkForUpdate resolved', { found });
        if (found) {
          this.markReady();
        } else {
          this.endPreparation();
        }
      },
      error => {
        logAppUpdate('checkForUpdate failed', error);
        this.endPreparation();
      }
    );
    return this.preparation;
  }

  /** Banner, idle, and hidden-tab reload. Always ends in a document reload. */
  async reloadNow(): Promise<void> {
    if (!this.canLeave()) {
      return;
    }
    logAppUpdate('reloadNow', { updateReady: this.updateReady });
    this.stateWritable.set('reloading');
    try {
      this.saveScroll();
      if (!this.updateReady && this.controlledWorker()) {
        await within(this.prepare(), APP_UPDATE_WAIT_MS);
      }
    } finally {
      this.reloadPage();
    }
  }

  /** Loads the target document instead of routing, so it boots the new build. */
  navigateWithUpdate(url: string): void {
    if (!this.canLeave()) {
      return;
    }
    logAppUpdate('navigateWithUpdate', { url });
    this.stateWritable.set('reloading');
    try {
      this.saveScroll();
    } finally {
      this.assignPage(url);
    }
  }

  /** Worker events, idle and hidden-tab reload, scroll restore. Call once from the app shell. */
  watch(): void {
    if (this.watching || !isPlatformBrowser(this.platformId)) {
      return;
    }
    this.watching = true;
    const sw = this.swUpdate;
    logAppUpdate('watching', {
      worker: sw?.isEnabled ?? false,
      controlled: !!navigator.serviceWorker?.controller,
    });
    if (sw?.isEnabled) {
      // VERSION_READY is sent once, when any tab or the worker itself installs a build.
      sw.versionUpdates
        .pipe(takeUntilDestroyed(this.destroyRef))
        .subscribe(event => this.onVersionEvent(event));
      this.startupCheck = true;
      void this.prepare().finally(() => {
        this.startupCheck = false;
      });
    }

    const abort = new AbortController();
    this.destroyRef.onDestroy(() => {
      abort.abort();
      this.clearIdle();
    });
    for (const type of ['pointerdown', 'keydown', 'pointermove', 'wheel']) {
      window.addEventListener(type, this.onActivity, {
        signal: abort.signal,
        passive: true,
      });
    }
    document.addEventListener('visibilitychange', this.onVisibility, {
      signal: abort.signal,
    });

    this.router.events
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe(event => {
        if (this.alive() && event instanceof NavigationEnd) {
          this.restoreScroll();
        }
      });
  }

  private onVersionEvent(event: VersionEvent): void {
    if (event.type === 'VERSION_DETECTED') {
      this.noteAnnounced();
      const state = this.stateWritable();
      if (state === 'idle' || state === 'checking') {
        this.stateWritable.set('downloading');
      }
    } else if (event.type === 'VERSION_READY') {
      this.markReady();
    } else if (event.type === 'VERSION_INSTALLATION_FAILED') {
      this.endPreparation();
    }
    logAppUpdate(event.type, {
      state: this.stateWritable(),
      updateReady: this.updateReady,
    });
  }

  private markReady(): void {
    if (this.updateReady) {
      this.endPreparation();
      return;
    }
    this.updateReady = true;
    this.noteAnnounced();
    if (this.stateWritable() !== 'reloading') {
      this.stateWritable.set('ready');
    }
    this.endPreparation();
    this.applyOnStartup();
  }

  private endPreparation(): void {
    const settle = this.settlePreparation;
    this.preparation = null;
    this.settlePreparation = null;
    const state = this.stateWritable();
    if (
      !this.updateReady &&
      (state === 'checking' || state === 'downloading')
    ) {
      this.stateWritable.set('idle');
      sessionStorage.removeItem(APP_UPDATE_AUTO_APPLY_KEY);
    }
    if (settle) {
      logAppUpdate('prepare finished', {
        state: this.stateWritable(),
        updateReady: this.updateReady,
      });
      settle();
    }
  }

  /**
   * A refresh right after a deploy is served by the previous build. Swap now,
   * before the user starts working, instead of asking for a second refresh.
   */
  private applyOnStartup(): void {
    if (!this.startupCheck || this.interacted) {
      return;
    }
    if (sessionStorage.getItem(APP_UPDATE_AUTO_APPLY_KEY)) {
      logAppUpdate('already applied once this session, leaving the banner');
      return;
    }
    sessionStorage.setItem(APP_UPDATE_AUTO_APPLY_KEY, '1');
    logAppUpdate('previous build served this load, applying the new one');
    void this.reloadNow();
  }

  /**
   * SwUpdate only talks to pages the worker controls. On an uncontrolled page
   * (first visit, hard refresh) its calls wait for a controller that never comes.
   */
  private controlledWorker(): SwUpdate | null {
    const sw = this.swUpdate;
    if (
      !sw?.isEnabled ||
      !isPlatformBrowser(this.platformId) ||
      !navigator.serviceWorker?.controller
    ) {
      return null;
    }
    return sw;
  }

  private canLeave(): boolean {
    return (
      this.stateWritable() !== 'reloading' &&
      this.alive() &&
      isPlatformBrowser(this.platformId)
    );
  }

  private noteAnnounced(): void {
    this.announced = true;
    if (
      isPlatformBrowser(this.platformId) &&
      document.visibilityState === 'hidden' &&
      this.hiddenAt == null
    ) {
      this.hiddenAt = Date.now();
    }
    this.armIdle();
  }

  private onActivity = (event: Event): void => {
    if (!this.alive()) {
      return;
    }
    if (event.type !== 'pointermove') {
      this.interacted = true;
    }
    if (!this.announced) {
      return;
    }
    const now = Date.now();
    if (now - this.lastActivityAt < 1000) {
      return;
    }
    this.lastActivityAt = now;
    this.armIdle();
  };

  private onVisibility = (): void => {
    if (!this.alive()) {
      return;
    }
    if (document.visibilityState === 'hidden') {
      this.hiddenAt = Date.now();
      return;
    }
    const hiddenAt = this.hiddenAt;
    this.hiddenAt = null;
    if (
      hiddenAt != null &&
      Date.now() - hiddenAt >= APP_UPDATE_HIDDEN_MS &&
      this.announced
    ) {
      void this.reloadNow();
      return;
    }
    void this.prepare();
  };

  private armIdle(): void {
    this.clearIdle();
    if (!this.announced || !isPlatformBrowser(this.platformId)) {
      return;
    }
    this.idleTimer = setTimeout(() => {
      if (this.alive()) {
        void this.reloadNow();
      }
    }, APP_UPDATE_IDLE_MS);
  }

  private clearIdle(): void {
    if (this.idleTimer != null) {
      clearTimeout(this.idleTimer);
      this.idleTimer = null;
    }
  }

  private saveScroll(): void {
    const container = document.querySelector(SCROLL_SELECTOR);
    const snapshot: ScrollSnapshot = {
      version: SCROLL_VERSION,
      url: this.router.url,
      scrollTop: container instanceof HTMLElement ? container.scrollTop : 0,
    };
    sessionStorage.setItem(APP_UPDATE_SCROLL_KEY, JSON.stringify(snapshot));
  }

  /** First NavigationEnd only. Retries until the list is tall enough or the user takes over. */
  private restoreScroll(): void {
    if (this.scrollRestored) {
      return;
    }
    this.scrollRestored = true;
    const snapshot = this.takeScrollSnapshot();
    if (!snapshot) {
      return;
    }
    const deadline = Date.now() + SCROLL_RESTORE_MS;
    const step = (): void => {
      const container = document.querySelector(SCROLL_SELECTOR);
      if (container instanceof HTMLElement) {
        container.scrollTop = snapshot.scrollTop;
        if (container.scrollTop >= snapshot.scrollTop - 1) {
          return;
        }
      }
      if (!this.interacted && Date.now() < deadline) {
        requestAnimationFrame(step);
      }
    };
    step();
  }

  private takeScrollSnapshot(): ScrollSnapshot | null {
    const raw = sessionStorage.getItem(APP_UPDATE_SCROLL_KEY);
    sessionStorage.removeItem(APP_UPDATE_SCROLL_KEY);
    if (!raw) {
      return null;
    }
    try {
      const snapshot = JSON.parse(raw) as ScrollSnapshot;
      return snapshot.version === SCROLL_VERSION &&
        snapshot.url === this.router.url
        ? snapshot
        : null;
    } catch {
      return null;
    }
  }

  private alive(): boolean {
    return this.serviceId === latestServiceId;
  }
}

function within(promise: Promise<void>, ms: number): Promise<void> {
  let timer: ReturnType<typeof setTimeout> | undefined;
  const timeout = new Promise<void>(resolve => {
    timer = setTimeout(resolve, ms);
  });
  return Promise.race([promise, timeout]).finally(() => clearTimeout(timer));
}
